import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  TextField,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Box,
  useMediaQuery,
  useTheme,
  Typography,
  Divider,
  Card,
  CardContent,
  Autocomplete,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  QuotationCreate,
  QuotationUpdate,
  GenerateQuotationNo,
  ApprovePartyList,
} from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function FreightEntryForm() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();

  // ---------- PARTY STATE ----------
  const [partyOptions, setPartyOptions] = useState([]);
  const [partyPage, setPartyPage] = useState(1);
  const [partySearch, setPartySearch] = useState("");
  const [partyLoading, setPartyLoading] = useState(false);
  const [selectedPartyObj, setSelectedPartyObj] = useState(null);

  // ---------- FORM STATE ----------
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [errors, setErrors] = useState({});

  // ---------- FINAL AGREED AMOUNT STATE ----------
  const [isFinalAgreedEditable, setIsFinalAgreedEditable] = useState(false);

  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };
  const storedDivision = getSelectedDivision();

  const [formData, setFormData] = useState({
    id: null,
    ref_no: "",
    date: "",
    to_name: "",
    to_title: "",
    to_company: "",
    to_address: "",
    contact_no: "",
    email: "",
    subject: "Supply, Manufacturing, Erection & Commissioning of EOT Crane",
    body: "We are pleased to submit our quotation for the supply of Boiler Pressure Parts as per your inquiry.",
    gst_percent: "",
    validity: "",
    pf: "",
    transport_insurance: "",
    delivery: "",
    warranty: "",
    inspection_required: false,
    total_amount: 0,
    final_agreed_amount: 0, // NEW
    created_by: localStorage.getItem("user_id"),
    division: storedDivision?.division_id || "",
    party: "",
    contact_persons_list: [],
    payment_terms: [
      { payment: "30 % interest-free advance along with the order. " },
      { payment: "30 % against drawing approval & Inspection of Raw material at our workshop. " },
      { payment: "40% Balance against receipt of Performa invoice Before Material Dispatch." },
    ],
    terms_and_conditions: [
      { key: "GST", value: "@ 18%  ", is_default: true },
      { key: "Validity", value: "10 Days", is_default: true },
      { key: "P & F", value: "EXCLUSIVE", is_default: true },
      { key: "Transportation and Insurance", value: "To Be arranged by Purchaser", is_default: true },
      {
        key: "Delivery",
        value:
          "14 - 16 Weeks Delivery period shall be reckoned from the date of advance payment / approved drawing / IBR approval, whichever is later.",
        is_default: true,
      },
      {
        key: "Bank Details",
        value: `CRANOIST MATERIAL HANDLING EQUIPMENTS PVT. LTD. 
BANK NAME: BANK OF MAHARASHTRA, A/C NO: 60493771870 
IFSC: MAHB0000057 BRANCH NAME: RK HEIGHTS, DURG`,
        is_default: true,
      },
    ],
    items: [
      {
        description: "",
        unit: "",
        quantity: 0,
        rate: 0,
        amount: 0,
      },
    ],
  });

  // ---------- API CALLS ----------
  const fetchNextQuotationNo = async () => {
    const data = await GenerateQuotationNo();
    if (data && data.ref_no) {
      setFormData((prev) => ({ ...prev, ref_no: data.ref_no }));
    }
  };

  const getParties = async (pageNum = 1, filters = {}) => {
    try {
      setPartyLoading(true);
      const response = await ApprovePartyList(pageNum, filters);
      const results = Array.isArray(response) ? response : [];
      let finalOptions = results;
      if (selectedPartyObj && !finalOptions.some((p) => p.id === selectedPartyObj.id)) {
        finalOptions = [selectedPartyObj, ...finalOptions];
      }
      if (pageNum === 1) {
        setPartyOptions(finalOptions);
      } else {
        setPartyOptions((prev) => {
          const merged = [...prev, ...finalOptions];
          if (selectedPartyObj && !merged.some((p) => p.id === selectedPartyObj.id)) {
            return [selectedPartyObj, ...merged];
          }
          return merged;
        });
      }
      setPartyPage(pageNum);
    } catch (err) {
      console.error("Error fetching parties:", err);
      toast.error("Failed to load parties");
    } finally {
      setPartyLoading(false);
    }
  };

  // ---------- EFFECTS ----------
  useEffect(() => {
    const quoteData = location.state?.data ?? null;
    if (!quoteData && !isEditMode) {
      fetchNextQuotationNo();
    }
    getParties(1, { name: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

useEffect(() => {
    const timer = setTimeout(() => {
      getParties(1, { name: partySearch }); // 👈 "search" की जगह "name"
    }, 500);
    return () => clearTimeout(timer);
  }, [partySearch]);

  const quoteData = location.state?.data ?? null;
  useEffect(() => {
    if (!quoteData) return;
    setIsEditMode(true);

    const normalizedItems =
      Array.isArray(quoteData.items_list) && quoteData.items_list.length > 0
        ? quoteData.items_list.map((item) => ({
            unit: item.unit || item.uom || "",
            description: item.description ?? "",
            quantity: parseFloat(item.quantity) || 0,
            rate: parseFloat(item.rate) || 0,
            amount: parseFloat(item.amount) || 0,
          }))
        : [
            {
              description: "",
              quantity: 0,
              rate: 0,
              amount: 0,
              unit: "",
            },
          ];

          const matchedParty = partyOptions.find(
    (p) => p.company_name === quoteData.to_company
  );


    const updatedData = {
      id: quoteData.quotation_id,
      ref_no: quoteData.ref_no,
      date: quoteData.date ? quoteData.date : "",
      to_name: quoteData.to_name || "",
      to_title: quoteData.to_title || "",
      to_company: quoteData.to_company || "",
      to_address: quoteData.to_address || "",
      contact_no: quoteData.contact_no || "",
      email: quoteData.email || "",
      subject: quoteData.subject || "",
      body: quoteData.body || "",
      gst_percent: quoteData.gst_percent || "",
      validity: quoteData.validity || "",
      pf: quoteData.pf || "",
      transport_insurance: quoteData.transport_insurance || "",
      delivery: quoteData.delivery || "",
      warranty: quoteData.warranty || "",
      inspection_required: quoteData.inspection_required || false,
      total_amount: quoteData.total_amount || 0,
      payment_terms: quoteData.payment_terms || [],
      scope_of_work: quoteData.scope_of_work || [],
      exclusions: quoteData.exclusions || [],
      terms_and_conditions: quoteData.terms_and_conditions || [],
      division: storedDivision?.division_id || "",
       party: matchedParty ? matchedParty.id : "", // ✅ SET PARTY ID
    contact_persons_list: matchedParty?.contact_persons || [], // ✅ SET CONTACT PERSONS
      items: normalizedItems,
    };

    setFormData(updatedData);
  }, [quoteData, partyOptions]);

  // ---------- HANDLERS ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    let error = "";
    let updatedValue = value;

    if (name === "contact_no") {
      updatedValue = value.replace(/\D/g, "");
      if (updatedValue.length > 10) {
        updatedValue = updatedValue.slice(0, 10);
      }
      if (updatedValue.length !== 10 && updatedValue.length > 0) {
        error = "Must be exactly 10 digits";
      } else {
        error = "";
      }
    }

    if (name === "email") {
      updatedValue = updatedValue.trim();
      if (updatedValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(updatedValue)) {
        error = "Invalid email format";
      } else {
        error = "";
      }
    }

    // Handle final agreed amount
    if (name === "final_agreed_amount") {
      updatedValue = parseFloat(value) || 0;
      setFormData((prev) => ({
        ...prev,
        final_agreed_amount: updatedValue,
      }));
      return; // early exit because we handle it separately
    }

    setFormData((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // Toggle handler for final agreed amount checkbox
  const handleFinalAgreedToggle = (e) => {
    const checked = e.target.checked;
    setIsFinalAgreedEditable(checked);
    if (!checked) {
      setFormData((prev) => ({
        ...prev,
        final_agreed_amount: 0,
      }));
    }
  };

  const handleQuotationSubmit = async (e) => {
    e.preventDefault();

    const userId = localStorage.getItem("user_id");
    if (!userId) {
      alert("User not logged in. Please login again.");
      return;
    }

    setLoading(true);
    try {
      const cleanedItems = formData.items.map((item) => ({
        ...item,
        unit: item.unit || item.uom,
      }));

      const payload = {
        ...formData,
        created_by: formData.created_by || userId,
        items: cleanedItems,
        to_title: formData.to_title || "",
        // ensure final_agreed_amount is sent
        final_agreed_amount: formData.final_agreed_amount || 0,
      };

      if (isEditMode) {
        await QuotationUpdate(payload.id, payload);
        toast.success("Quotation updated successfully!", {
          onClose: () => navigate("/dashboard/quotation"),
          autoClose: 2000,
        });
      } else {
        await QuotationCreate(payload);
        toast.success("Quotation created successfully!", {
          onClose: () => navigate("/dashboard/quotation"),
          autoClose: 2000,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong!", { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWithConfirmation = (sectionKey, idx) => {
    const updated = [...formData[sectionKey]];
    updated.splice(idx, 1);
    setFormData({ ...formData, [sectionKey]: updated });
  };

  const handleItemChange = (e, index) => {
    const { name, value } = e.target;
    const updatedItems = [...formData.items];

    const updatedValue =
      name === "description" || name === "unit" ? value : Number(value) || 0;

    updatedItems[index][name] = updatedValue;

    if (name === "quantity" || name === "rate") {
      const qty = Number(updatedItems[index].quantity) || 0;
      const rate = Number(updatedItems[index].rate) || 0;
      updatedItems[index].amount = qty * rate;
    }

    const totalAmount = updatedItems.reduce(
      (sum, item) => sum + (Number(item.amount) || 0),
      0
    );

    setFormData((prev) => ({
      ...prev,
      items: updatedItems,
      total_amount: totalAmount,
    }));
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          description: "",
          unit: "",
          quantity: 0,
          rate: 0,
          amount: 0,
        },
      ],
    }));
  };

  const deleteItem = (indexToDelete) => {
    const updatedItems = formData.items.filter((_, i) => i !== indexToDelete);
    const totalAmount = updatedItems.reduce(
      (sum, item) => sum + (Number(item.amount) || 0),
      0
    );
    setFormData((prev) => ({
      ...prev,
      items: updatedItems,
      total_amount: totalAmount,
    }));
  };

  const handleTermChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedTerms = [...prev.terms_and_conditions];
      updatedTerms[index][field] = value;
      return {
        ...prev,
        terms_and_conditions: updatedTerms,
      };
    });
  };

  const addTerm = () => {
    setFormData((prev) => ({
      ...prev,
      terms_and_conditions: [
        ...prev.terms_and_conditions,
        { key: "", value: "", is_default: false },
      ],
    }));
  };

  const removeTerm = (index) => {
    setFormData((prev) => ({
      ...prev,
      terms_and_conditions: prev.terms_and_conditions.filter((_, i) => i !== index),
    }));
  };

  const keyLabelMap = {
    payment_terms: { fieldKey: "payment", label: "Payment" },
  };

  // ---------- RENDER ----------
  return (
    <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 2 }, py: 2 }}>
      <form onSubmit={handleQuotationSubmit} autoComplete="off">
        {/* SUBMIT BUTTON */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mb: 2,
            mt: { xs: 2, md: -3 },
          }}
        >
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            fullWidth={isMobile}
            sx={{ height: { xs: 40, md: 36 } }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : isEditMode ? (
              "Update"
            ) : (
              "Submit"
            )}
          </Button>
        </Box>

        {/* HEADER INFO */}
        <Box border="1px solid #ccc" borderRadius="8px" mb={2} p={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="Quotation Ref No"
                name="ref_no"
                value={formData.ref_no}
                onChange={handleChange}
                required
                disabled={true}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="Date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* DEBTOR AUTOCOMPLETE */}
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={partyOptions}
                getOptionLabel={(option) => option.company_name || ""}
                filterOptions={(options) => options}
                value={
                  partyOptions.find((option) => option.id === formData.party) ||
                  null
                }
                onChange={(event, newValue) => {
                  if (newValue) {
                    const contactPerson = newValue.contact_persons?.[0] || {};
                    setFormData((prev) => ({
                      ...prev,
                      party: newValue.id,
                      to_company: newValue.company_name || "",
                      to_name: contactPerson.contact_name || "",
                      to_address: newValue.address || "",
                      contact_no: newValue.contact || contactPerson.contact_no || "",
                      email: newValue.email || "",
                      contact_persons_list: newValue.contact_persons || [],
                    }));
                    setSelectedPartyObj(newValue);
                  } else {
                    setFormData((prev) => ({
                      ...prev,
                      party: "",
                      to_company: "",
                      to_name: "",
                      to_address: "",
                      contact_no: "",
                      email: "",
                      contact_persons_list: [],
                    }));
                    setSelectedPartyObj(null);
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setPartySearch(newInputValue);
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body1">{option.company_name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {option.name} {option.contact && `| ${option.contact}`}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company Name"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {partyLoading && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* TO NAME - Contact Person Dropdown */}
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={formData.contact_persons_list || []}
                getOptionLabel={(option) => {
                  return option.title ? `${option.title} ${option.contact_name}` : option.contact_name || "";
                }}
                value={
                  formData.contact_persons_list?.find(
                    (p) => {
                      const fullName = p.title ? `${p.title} ${p.contact_name}` : p.contact_name;
                      return fullName === formData.to_name;
                    }
                  ) || null
                }
                onChange={(event, newValue) => {
                  if (newValue) {
                    setFormData((prev) => ({
                      ...prev,
                      to_name: newValue.title ? `${newValue.title} ${newValue.contact_name}` : newValue.contact_name,
                      contact_no: newValue.contact_no || prev.contact_no,
                      to_title: newValue.title || "",
                    }));
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body2">
                        {option.title ? `${option.title} ` : ""}{option.contact_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {option.designation || "No designation"} 
                        {option.contact_no && ` | ${option.contact_no}`}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="To Name"
                    required
                    size={isMobile ? "small" : "medium"}
                    disabled={!formData.party}
                    helperText={!formData.party ? "Select a debtor first" : ""}
                  />
                )}
                isOptionEqualToValue={(option, value) => {
                  const optName = option.title ? `${option.title} ${option.contact_name}` : option.contact_name;
                  const valName = value?.title ? `${value.title} ${value.contact_name}` : value?.contact_name;
                  return optName === valName;
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="Contact No."
                name="contact_no"
                value={formData.contact_no}
                onChange={handleChange}
                error={Boolean(errors.contact_no)}
                helperText={errors.contact_no}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={Boolean(errors.email)}
                helperText={errors.email}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6} md={12}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="To Address"
                name="to_address"
                value={formData.to_address}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6} md={12}>
              <TextField
                defaultValue={" OFFER FOR SUPPLY OF SUPRE HEATER SPARES- BENDS SET"}
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="Subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="Body"
                name="body"
                value={formData.body}
                onChange={handleChange}
                multiline
                rows={isMobile ? 1 : 0}
              />
            </Grid>
          </Grid>
        </Box>

        {/* ITEMS SECTION */}
        {isMobile ? (
          <Box sx={{ mb: 2 }}>
            {formData.items.map((item, index) => (
              <Card key={index} sx={{ mb: 2, boxShadow: 2 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      Item #{index + 1}
                    </Typography>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => deleteItem(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Description"
                        name="description"
                        value={item.description}
                        onChange={(e) => handleItemChange(e, index)}
                        multiline
                        maxRows={3}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Quantity"
                        name="quantity"
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(e, index)}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Unit"
                        name="unit"
                        value={item.unit}
                        onChange={(e) => handleItemChange(e, index)}
                        required
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Rate"
                        name="rate"
                        type="number"
                        value={item.rate}
                        onChange={(e) => handleItemChange(e, index)}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Amount"
                        name="amount"
                        type="number"
                        value={item.amount}
                        onChange={(e) => handleItemChange(e, index)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}

            <Button
              onClick={addItem}
              variant="outlined"
              fullWidth
              startIcon={<AddIcon />}
              sx={{ mb: 2 }}
            >
              Add Item
            </Button>

            <Box
              sx={{
                bgcolor: "#f5f5f5",
                p: 2,
                borderRadius: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Grand Total
              </Typography>
              <Typography variant="h6" color="primary" fontWeight="bold">
                ₹ {formData.total_amount.toFixed(2)}
              </Typography>
            </Box>

            {/* FINAL AGREED AMOUNT - MOBILE */}
            <Box
              sx={{
                bgcolor: "#f5f5f5",
                p: 2,
                borderRadius: 1,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                gap: 1,
                mt: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isFinalAgreedEditable}
                    onChange={handleFinalAgreedToggle}
                    color="primary"
                  />
                }
                label="Enable Final Agreed Amount"
              />
              <TextField
                fullWidth
                size="small"
                label="Final Agreed Amount"
                name="final_agreed_amount"
                type="number"
                value={formData.final_agreed_amount || 0}
                onChange={handleChange}
                disabled={!isFinalAgreedEditable}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
                }}
              />
            </Box>
          </Box>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{
                position: "relative",
                mb: 2,
                overflowX: "auto",
              }}
            >
              <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "40%" }}>Description</TableCell>
                    <TableCell sx={{ width: "10%" }}>Qty</TableCell>
                    <TableCell sx={{ width: "15%" }}>Unit</TableCell>
                    <TableCell sx={{ width: "12%" }}>Rate</TableCell>
                    <TableCell sx={{ width: "15%" }}>Total</TableCell>
                    <TableCell sx={{ width: "8%" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          name="description"
                          value={item.description || ""}
                          onChange={(e) => handleItemChange(e, index)}
                          multiline
                          maxRows={3}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          name="quantity"
                          type="number"
                          value={item.quantity || ""}
                          onChange={(e) => handleItemChange(e, index)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          name="unit"
                          value={item.unit || ""}
                          onChange={(e) => handleItemChange(e, index)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          name="rate"
                          type="number"
                          value={item.rate || ""}
                          onChange={(e) => handleItemChange(e, index)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          name="amount"
                          type="number"
                          value={item.amount || ""}
                          onChange={(e) => handleItemChange(e, index)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="error"
                          onClick={() => deleteItem(index)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* GRAND TOTAL */}
                  <TableRow
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      backgroundColor: "#fff",
                      zIndex: 3,
                    }}
                  >
                    <TableCell
                      colSpan={4}
                      align="right"
                      sx={{ backgroundColor: "#fff" }}
                    >
                      <strong>Grand Total</strong>
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#fff" }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={formData.total_amount || 0}
                        disabled
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#fff" }} />
                  </TableRow>

                  {/* FINAL AGREED AMOUNT - DESKTOP */}
                  <TableRow
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      backgroundColor: "#fff",
                      zIndex: 3,
                    }}
                  >
                    <TableCell
                      colSpan={2}
                      sx={{ backgroundColor: "#fff", borderBottom: "none" }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isFinalAgreedEditable}
                            onChange={handleFinalAgreedToggle}
                            color="primary"
                            size="small"
                          />
                        }
                        label="Enable Final Agreed Amount"
                      />
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      align="right"
                      sx={{ backgroundColor: "#fff", borderBottom: "none" }}
                    >
                      <strong>Final Agreed Amount</strong>
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#fff", borderBottom: "none" }}>
                      <TextField
                        fullWidth
                        size="small"
                        name="final_agreed_amount"
                        type="number"
                        value={formData.final_agreed_amount || 0}
                        onChange={handleChange}
                        disabled={!isFinalAgreedEditable}
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#fff", borderBottom: "none" }} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Button onClick={addItem} variant="outlined" sx={{ mb: 2 }}>
              + Add Item
            </Button>
          </>
        )}

        {/* TERMS & CONDITIONS + OTHER SECTIONS */}
        <Grid item xs={12}>
          <Box mt={3}>
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              fontWeight="bold"
              mb={2}
              sx={{
                borderBottom: "2px solid #1976d2",
                paddingBottom: 1,
                display: "inline-block",
              }}
            >
              Terms & Conditions
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: "#f9f9f9",
                borderRadius: 2,
                border: "1px solid #e0e0e0",
              }}
            >
              {formData.terms_and_conditions.map((term, index) => (
                <Grid
                  container
                  spacing={isMobile ? 1 : 2}
                  key={index}
                  alignItems="center"
                  sx={{
                    mb: 1.5,
                    flexWrap: isMobile ? "wrap" : "nowrap",
                  }}
                >
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      value={term.key}
                      onChange={(e) =>
                        handleTermChange(index, "key", e.target.value)
                      }
                      placeholder="Term Title"
                      label={isMobile ? "Term" : ""}
                      variant="outlined"
                      sx={{
                        backgroundColor: term.is_default ? "#f0f0f0" : "#fff",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={10} sm={8}>
                    <TextField
                      fullWidth
                      size="small"
                      value={term.value}
                      onChange={(e) =>
                        handleTermChange(index, "value", e.target.value)
                      }
                      placeholder="Enter condition/value"
                      label={isMobile ? "Value" : ""}
                      variant="outlined"
                      multiline={term.key === "Bank Details"}
                      minRows={term.key === "Bank Details" ? 3 : 1}
                      maxRows={term.key === "Bank Details" ? 5 : 1}
                      sx={{
                        backgroundColor: "#fff",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1,
                        },
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    sm={1}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <IconButton
                      onClick={() => removeTerm(index)}
                      color="error"
                      size="small"
                      sx={{
                        backgroundColor: term.is_default ? "#e0e0e0" : "#ffebee",
                        "&:hover": {
                          backgroundColor: term.is_default ? "#e0e0e0" : "#ffcdd2",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}

              <Box
                mt={2}
                display="flex"
                justifyContent={isMobile ? "center" : "flex-start"}
              >
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addTerm}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Add More Terms
                </Button>
              </Box>
            </Paper>

            <Box border="1px solid #ccc" borderRadius="8px" p={2} marginTop="5px">
              <Grid container spacing={2}>
                {Object.entries(keyLabelMap).map(
                  ([sectionKey, { fieldKey, label }]) => (
                    <Grid item xs={12} md={4} mt={2} key={sectionKey}>
                      {formData[sectionKey].map((item, idx) => (
                        <Box
                          key={idx}
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <TextField
                            fullWidth
                            size={isMobile ? "small" : "medium"}
                            label={label}
                            value={item[fieldKey]}
                            error={!item[fieldKey].trim()}
                            helperText={
                              !item[fieldKey].trim()
                                ? `${label} cannot be empty`
                                : ""
                            }
                            onChange={(e) => {
                              const updated = [...formData[sectionKey]];
                              updated[idx][fieldKey] = e.target.value;
                              setFormData({ ...formData, [sectionKey]: updated });
                            }}
                          />
                          <Box
                            sx={{ display: "flex", flexDirection: "column", ml: 1 }}
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeleteWithConfirmation(sectionKey, idx)
                              }
                              disabled={formData[sectionKey].length === 1}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                            {idx === formData[sectionKey].length - 1 && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const updated = [...formData[sectionKey]];
                                  updated.push({ [fieldKey]: "" });
                                  setFormData({
                                    ...formData,
                                    [sectionKey]: updated,
                                  });
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Grid>
                  )
                )}
              </Grid>
            </Box>
          </Box>
        </Grid>

        <ToastContainer position="top-right" autoClose={3000} />
      </form>
    </Container>
  );
}